import axios from "axios";
import { handleActions } from "redux-actions";

import Config from "config";
import { getHeaders } from "helpers/headers";
import { AnyAction } from "interfaces/action";
import { Dispatch } from "interfaces/dispatch";
import { Location, UserLocation } from "interfaces/location";
import { ThunkActionCreator } from "interfaces/thunk";

// Actions Types
export const UPDATE_USER_LOCATION_REQUEST = "UPDATE_USER_LOCATION_REQUEST";
export const UPDATE_USER_LOCATION = "UPDATE_USER_LOCATION";
export const UPDATE_USER_LOCATION_FAILED = "UPDATE_USER_LOCATION_FAILED";

/** Puts zip + coords in Redux (e.g. after nearby-zips when zip-codes had no single zip). */
const setUserLocationZip: ThunkActionCreator<void> =
  (zip: string, currentCoords: UserLocation) => (dispatch: Dispatch) => {
    if (!zip) return;
    dispatch({
      type: UPDATE_USER_LOCATION,
      payload: { zip, currentCoordinates: currentCoords },
    });
  };

// Thunk Action Creator
const getUserZipCode: ThunkActionCreator<Promise<string>> =
  (currentCoords: UserLocation) => (dispatch: Dispatch) => {
    dispatch({ type: UPDATE_USER_LOCATION_REQUEST });
    return axios
      .get(`${Config.API_SERVICE_URL}/api/locations/zip-codes`, {
        ...getHeaders(),
        params: {
          latitude: currentCoords.latitude,
          longitude: currentCoords.longitude,
        },
      })
      .then((response) => {
        // 204 = reverse geocode found no zip for this point — not a hard failure:
        // /nearby-zips may still return zips. Do not set location.error.
        if (response.status === 204) {
          dispatch({
            type: UPDATE_USER_LOCATION,
            payload: { currentCoordinates: currentCoords },
          });
          return "";
        }
        dispatch({
          type: UPDATE_USER_LOCATION,
          payload: { ...response.data, currentCoordinates: currentCoords },
        });
        return (response.data?.zip as string) || "";
      })
      .catch(() => {
        // Network / HTTP error on zip-codes — keep coords so parallel nearby-zips can still win.
        dispatch({
          type: UPDATE_USER_LOCATION,
          payload: { currentCoordinates: currentCoords },
        });
        return "";
      });
  };

/**
 * Calls /api/locations/nearby-zips with the user's coordinates and returns the
 * list of postal codes whose centres are within <radiusMiles> of the user.
 * Falls back to an empty array on any error so the caller can degrade gracefully.
 */
const getNearbyZips: ThunkActionCreator<Promise<string[]>> =
  (coords: UserLocation, radiusMiles = 6, sampleCount?: number) => (_dispatch: Dispatch) => {
    return axios
      .get(`${Config.API_SERVICE_URL}/api/locations/nearby-zips`, {
        ...getHeaders(),
        params: {
          latitude: coords.latitude,
          longitude: coords.longitude,
          radius: radiusMiles,
          ...(sampleCount != null && sampleCount > 0 ? { count: sampleCount } : {}),
        },
      })
      .then((response) => {
        if (response.status === 204 || !response.data?.zips) return [] as string[];
        return response.data.zips as string[];
      })
      .catch(() => [] as string[]);
  };

const getLatLongFromZip: ThunkActionCreator<Promise<any>> =
  (zip: string) => (dispatch: Dispatch) => {
    dispatch({ type: UPDATE_USER_LOCATION_REQUEST });
    return axios
      .get(`${Config.API_SERVICE_URL}/api/locations/lat-long-from-zip`, {
        ...getHeaders(),
        params: {
          zip,
        },
      })
      .then((response) => {
        if (response.status === 204) {
          throw dispatch({ type: UPDATE_USER_LOCATION_FAILED });
        } else {
          return dispatch({
            type: UPDATE_USER_LOCATION,
            payload: response.data,
          });
        }
      })
      .catch(() =>
        dispatch({
          type: UPDATE_USER_LOCATION_FAILED,
        })
      );
  };

export { getUserZipCode, getNearbyZips, getLatLongFromZip, setUserLocationZip };

// Actions
const actions = {
  [UPDATE_USER_LOCATION_REQUEST]: (state: LocationState): LocationState => ({
    ...state,
    loading: true,
    error: false,
  }),
  [UPDATE_USER_LOCATION]: (state: LocationState, action: AnyAction): LocationState => ({
    ...state,
    loading: false,
    error: false,
    content: action.payload,
  }),
  [UPDATE_USER_LOCATION_FAILED]: (state: LocationState, action: AnyAction): LocationState => ({
    ...state,
    loading: false,
    error: true,
    content: undefined,
  }),
};

// Reducer Interface
export interface LocationState {
  error?: boolean;
  loading?: boolean;
  content?: Location;
}

// Initial State
const initialState: LocationState = {
  loading: false,
  error: false,
  content: undefined,
};

// Reducer
export default handleActions<LocationState, AnyAction>(actions, initialState);
